import React, { useLayoutEffect, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '../../Card/Card.js';
import CardHeader from '../../Card/CardHeader.js';
import apiService from '../../../services/api.service';
import { useHistory } from 'react-router';
import CardBody from '../../Card/CardBody';
import { useState } from 'react';
import helpService from '../../../services/help.service';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ThemeProvider } from '@material-ui/styles';
import { lightTheme } from '../../../themes/lightTheme';
import ContentFont from '../../Typography/ContentFont';
import ChartLogo from '../../parts/ChartLogo';
import LoadingOverlay from '../../parts/LoadingOverlay.js';
import ChartNoData from '../../parts/ChartNoData.js';
import { Link } from 'react-router-dom';
import { format } from 'react-string-format';

const useStyles = makeStyles((theme) => ({
  chart: {
    position: 'relative',
    height: '500px',
    marginTop: '-20px',
    marginBottom: '0px',
  },
}));

function JobTypeSupplierChart(props) {
  const classes = useStyles();
  const [chartDataInput, setChartDataInput] = useState([]);
  const { FilterComponentData, baseQueryString } = props;
  const [dataLength, setDataLength] = useState(0);
  const [scaleSet, setScaleSet] = useState(1);
  const scaleWidth = 40;
  const labelLimit = 5;
  const [showNoData, setShowNoData] = useState(false);
  const [IsLoading, setIsLoading] = useState();

  const history = useHistory();

  function hasChartData() {
    if (chartDataInput && chartDataInput.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  function getDetailsPageUrl(supplierfullname, mode) {
    if (supplierfullname && mode) {
      let title = format('Mode = {0}', mode);
      return format('/shipments?mode={0}&s={1}&{2}&sst={3}', mode, supplierfullname, baseQueryString, title);
    } else {
      return format('/shipments?&{0}', baseQueryString);
    }
  }

  useEffect(() => {
    let isCancelled = false;
    if (FilterComponentData.accountId && FilterComponentData.start && FilterComponentData.end) {
      setIsLoading(true);
      apiService
        .getJobTypePerSupplierReport(
          FilterComponentData.accountId,
          FilterComponentData.dateBasedOn,
          FilterComponentData.start,
          FilterComponentData.end,
          FilterComponentData.suppliers
        )
        .then((result) => {
          if (!isCancelled) {
            if (result.data.jobTypePerSupplierReportItems != '') {
              setDataLength(result.data.jobTypePerSupplierReportItems.length);
              if (result.data.jobTypePerSupplierReportItems.length > scaleWidth) {
                setScaleSet((scaleWidth / result.data.jobTypePerSupplierReportItems.length).toFixed(1));
              } else {
                setScaleSet(1);
              }
              setShowNoData(false);
            } else {
              setScaleSet(1);
              setDataLength(0);
              setShowNoData(true);
            }

            setChartDataInput(result.data.jobTypePerSupplierReportItems);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) history.push('/login');
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [FilterComponentData, history]);

  useLayoutEffect(() => {
    var chart;
    if (hasChartData()) {
      var colorList = [];
      for (var i = 0; i < lightTheme.chart.colors.length; i++) {
        colorList.push(am4core.color(lightTheme.chart.colors[i]));
      }

      chart = am4core.create('SeriesXYChartStacked', am4charts.XYChart);
      chart.logo.disabled = true;

      chart.colors.list = colorList;

      // Add data
      chart.data = prepareStuff(chartDataInput);

      chart.legend = new am4charts.Legend();
      chart.legend.position = 'top';
      chart.legend.marginTop = 30;
      chart.legend.paddingBottom = 20;
      chart.legend.labels.template.maxWidth = 70;

      // Create axes
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'supplier';
      categoryAxis.title.text = '';
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.title.position = 'top';

      categoryAxis.renderer.cellStartLocation = 0.1;
      categoryAxis.renderer.cellEndLocation = 0.9;
      categoryAxis.renderer.minGridDistance = 30;

      if (dataLength > labelLimit) {
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.verticalCenter = 'middle';
        categoryAxis.fontSize = 11;
      } else categoryAxis.fontSize = 14;

      categoryAxis.renderer.labels.template.rotation = dataLength < labelLimit ? 0 : 315;
      categoryAxis.tooltip.disabled = false;

      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.title.text = '';

      // Create series
      function createSeries(field, name, stacked) {
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = field;
        series.dataFields.categoryX = 'supplier';
        series.dataFields.categoryX2 = 'supplierfullname';
        series.name = name;
        series.tooltipText = '{categoryX2} - {name}: [bold]{valueY}[/]';
        series.stacked = stacked;
        series.columns.template.strokeWidth = 0;
        series.fontSize = 10;
        series.columns.template.url = getDetailsPageUrl('{supplierfullname}', field);
      }

      createSeries('BCN', 'BCN', true);
      createSeries('FCL', 'FCL', true);
      createSeries('LCL', 'LCL', true);
      createSeries('LSE', 'LSE', true);

      // Add Scrollbar & cursor
      chart.cursor = new am4charts.XYCursor();
      chart.scrollbarX = new am4core.Scrollbar();
      chart.scrollbarX.visible = scaleSet < 1;
      chart.scrollbarX.end = scaleSet;
    }
    return () => {
      if (chart) chart.dispose();
    };
  }, [chartDataInput, showNoData]);

  const prepareStuff = (data) => {
    var tempArray1 = [];
    data.forEach((item) => {
      let itemObj = {
        supplier: dataLength < labelLimit ? item.supplier : helpService.truncateString(item.supplier, 15),
        supplierfullname: item.supplier,
        BCN: 0,
        FCL: 0,
        LCL: 0,
        LSE: 0,
      };
      item.jobTypePerSupplierReportItemTypes.forEach((jobType) => {
        itemObj[jobType['jobType']] = jobType['count'];
      });
      tempArray1.push(itemObj);
    });

    return tempArray1;
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Card chart>
        <LoadingOverlay IsLoading={IsLoading} />
        <CardHeader className={'dashboard-chart-area'}>
          <ContentFont>{showNoData ? <ChartNoData /> : <div id="SeriesXYChartStacked" className={classes.chart}></div>}</ContentFont>
        </CardHeader>
        <CardBody className={'dashboard-chart-title-area'}>
          <Link to={getDetailsPageUrl()}>
            <h4>Job Types Per Supplier</h4>
          </Link>
          <ChartLogo />
        </CardBody>
      </Card>
    </ThemeProvider>
  );
}

export default JobTypeSupplierChart;
